import React, { useMemo, useState, useRef, useEffect } from 'react';
import _ from 'lodash';
import { getSystemLocale } from './i18n';

const windowThis = window as any;

interface Props {
  language: string;
  resourcePath: string;
  children?: React.ReactNode;
}

interface LangState {
  prevLang: string;
  currLang: string;
}

interface I18nState {
  language: string;
  resourcePath: string;
  resources: I18nResources;
  changeLanguage?: (v: string) => void;
  updateResources: (lang: string, keys: string[], resources: any[]) => void;
  usedNamespaces?: React.MutableRefObject<Record<string, string[]>>;
  updateNamespaces?: (lang: string, v: string | string[]) => void;
  displayMode?: 'key' | 'value';
}

interface I18nResources {
  [key: string]: {
    [key: string]: {
      [key: string]: string;
    };
  };
}

const defaultI18nState = {
  language: 'zh-CN',
  resourcePath: '',
  resources: {},
  updateResources: () => {},
  displayMode: windowThis.MIOTECH_I18N_MODE || 'value',
};

export let langState: LangState = {
  prevLang: '',
  currLang: '',
};

let i18nInstance: I18nState;

const I18nContext = React.createContext<I18nState>(defaultI18nState);

if (!windowThis.magic) {
  windowThis.magic = () => {
    windowThis.MIOTECH_I18N_MODE =
      windowThis.MIOTECH_I18N_MODE === 'key' ? 'value' : 'key';
    Object.values(windowThis.i18nSetFunctionMap || {}).forEach(
      (func: any) => func && func(windowThis.MIOTECH_I18N_MODE),
    );
  };
}

export const I18nContextProvider = (props: Props) => {
  const { language = getSystemLocale(), resourcePath, children } = props;
  const [resources, setResources] = useState<I18nResources>({});
  const [displayMode, setDisplayMode] = useState<'key' | 'value'>(
    windowThis.MIOTECH_I18N_MODE || 'value',
  );

  const usedNamespaces = useRef<Record<string, string[]>>({});
  const updateNamespaces = (lang: string, v: string | string[]) => {
    const values = typeof v === 'string' ? [v] : v;

    if (!usedNamespaces.current[lang]) {
      usedNamespaces.current[lang] = [];
    }
    usedNamespaces.current[lang] = _.union(
      usedNamespaces.current[lang],
      values,
    );
  };

  if (langState.currLang === '') {
    langState.currLang = language;
  } else if (langState.currLang !== language) {
    langState.prevLang = langState.currLang;
    langState.currLang = language;
  }

  const state = useMemo(
    () => ({
      language,
      resourcePath,
    }),
    [language, resourcePath],
  );

  useEffect(() => {
    if (!windowThis.i18nSetFunctionMap) {
      windowThis.i18nSetFunctionMap = {};
    }
    windowThis.i18nSetFunctionMap[resourcePath] = setDisplayMode;

    return () => {
      if (windowThis.i18nSetFunctionMap) {
        windowThis.i18nSetFunctionMap[resourcePath] = null;
      }
    };
  }, [resourcePath]);

  return (
    <I18nContext.Provider
      value={{
        ...state,
        resources,
        displayMode,
        updateResources: (lang, keys: string[], newRes: any[]) => {
          setResources(prev => {
            const res = _.clone(prev);
            if (!res[lang]) res[lang] = {};
            keys.forEach((ns, index) => {
              res[lang][ns] = newRes[index];
            });
            return res;
          });
        },
        usedNamespaces,
        updateNamespaces,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
};

export function useI18nContext() {
  const context = React.useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used with in a I18nProvider');
  }
  return context;
}

export function setI18n(instance: I18nState) {
  i18nInstance = instance;
}

export function getI18n() {
  return i18nInstance;
}
